// src/App.jsx - Unified responsive application
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useUserData } from "./hooks/useUserData";
import { useEffect, useState } from "react";
import { setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { auth } from "./services/firebase";

// Layouts
import AdminLayout from "./layouts/AdminLayout";
import StudentLayout from "./layouts/StudentLayout";
import TechnicianLayout from "./layouts/TechnicianLayout";

// Pages - Unified imports from both systems
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import StudentProfileSetup from "./pages/StudentProfileSetup";

import StudentDashboard from "./pages/StudentDashboard";
import StudentAssets from "./pages/StudentAssets";
import StudentRequests from "./pages/StudentRequests";
import StudentReturnScan from "./pages/StudentReturnScan";
import Requests from "./pages/Requests";
import BrowseAssets from "./pages/BrowseAssets";

// Technician Pages
import TechnicianDashboard from "./pages/TechnicianDashboard";
import TechnicianAssets from "./pages/TechnicianAssets";
import TechnicianMaintenance from "./pages/TechnicianMaintenance";
import TechnicianReports from "./pages/TechnicianReports";
import TechnicianQRScanner from "./pages/TechnicianQRScanner";
import TechnicianNotifications from "./pages/TechnicianNotifications";
import TechnicianSchedule from "./pages/TechnicianSchedule";
import TechnicianValidation from "./pages/TechnicianValidation";
import TechnicianTasks from "./pages/TechnicianTasks";
import TechnicianTaskDetail from "./pages/TechnicianTaskDetail";

// Admin Pages & Components
import AdminPanel from "./pages/AdminPanel";
import AdminPasswordRequests from "./pages/AdminPasswordRequests"; // ✅ Added missing import
import AssetManager from "./components/admin/AssetManager";
import UserAdministration from "./components/admin/UserAdministration";
import RequestApproval from "./components/admin/RequestApproval";
import MapEditor from "./components/admin/MapEditor";
import QRTools from "./components/admin/QRTools";
import AuditTrail from "./components/admin/AuditTrail";
import EndWeekReport from "./components/admin/EndWeekReport";
import DisposedAssets from "./components/admin/DisposedAssets";

import AccountSettings from "./pages/AccountSettings";
import ItemRequest from "./pages/ItemRequest";

function AppContent() {
	const [user, userLoading] = useUserData();
	const [loadingTimeout, setLoadingTimeout] = useState(false);

	useEffect(() => {
		if (userLoading) {
			const timer = setTimeout(() => {
				setLoadingTimeout(true);
			}, 10000); // 10 seconds timeout

			return () => clearTimeout(timer);
		} else {
			setLoadingTimeout(false);
		}
	}, [userLoading]);

	if (userLoading) {
		if (loadingTimeout) {
			// Show error and redirect to login after timeout
			return (
				<div className="flex flex-col items-center justify-center h-screen bg-red-50">
					<div className="text-center p-6">
						<div className="text-red-600 text-6xl mb-4">⚠️</div>
						<h2 className="text-xl font-bold text-red-800 mb-2">Login Failed</h2>
						<p className="text-red-600 mb-4">Loading took too long. Please try again.</p>
						<button
							onClick={() => window.location.href = '/login'}
							className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
						>
							Go to Login
						</button>
					</div>
				</div>
			);
		}

		return (
			<div className="flex items-center justify-center h-screen text-gray-600">
				Loading...
			</div>
		);
	}

	// Logout handler
	const handleLogout = () => {
		localStorage.removeItem('currentUser');
		window.location.href = '/login';
	};

	return (
		<Router>
			<Routes>
				{/* Public Routes */}
				<Route path="/login" element={<Login />} />
				<Route path="/forgot-password" element={<ForgotPassword />} />

				{/* Admin Routes with Layout */}
				<Route
					path="/admin/*"
					element={
						user?.role === "admin" ? (
							<AdminLayout user={user} onLogout={handleLogout} />
						) : (
							<Navigate to="/login" replace />
						)
					}
				>
					<Route index element={<AdminPanel user={user} />} />
					<Route path="asset-manager" element={<AssetManager />} />
					<Route path="disposed-assets" element={<DisposedAssets />} />
					<Route path="user-administration" element={<UserAdministration />} />
					<Route path="request-approval" element={<RequestApproval />} />
					<Route path="map-editor" element={<MapEditor />} />
					<Route path="qr-tools" element={<QRTools />} />
					<Route path="audit-trail" element={<AuditTrail />} />
					<Route path="end-week-report" element={<EndWeekReport />} />
					<Route path="settings" element={<AccountSettings />} />
					<Route path="password-requests" element={<AdminPasswordRequests />} />
				</Route>

				{/* Student Routes with Layout */}
				<Route
					path="/student/*"
					element={
						user?.role === "student" ? (
							<StudentLayout user={user} onLogout={handleLogout} />
						) : (
							<Navigate to="/login" replace />
						)
					}
				>
					<Route index element={<StudentDashboard user={user} />} />
					<Route path="browse" element={<BrowseAssets />} />
					<Route path="item-request" element={<ItemRequest />} />
					<Route path="requests" element={<Requests />} />
					<Route path="asset-list" element={<StudentAssets user={user} />} />
					<Route path="return-scan" element={<StudentReturnScan />} />

					<Route path="settings" element={<AccountSettings />} />
				</Route>

				{/* Technician Routes with Layout */}
				<Route
					path="/technician/*"
					element={
						user?.role === "technician" ? (
							<TechnicianLayout user={user} onLogout={handleLogout} />
						) : (
							<Navigate to="/login" replace />
						)
					}
				>
					<Route index element={<TechnicianDashboard user={user} />} />
					<Route path="tasks" element={<TechnicianTasks user={user} />} />
					<Route path="task/:taskId" element={<TechnicianTaskDetail />} />
					<Route path="scan" element={<TechnicianQRScanner />} />
					<Route path="reports" element={<TechnicianReports />} />
					<Route path="settings" element={<AccountSettings />} />
				</Route>

				{/* Special Routes */}
				<Route
					path="/student/profile-setup"
					element={
						<ProtectedRoute user={user}>
							<StudentProfileSetup />
						</ProtectedRoute>
					}
				/>

				{/* Legacy Mobile Routes - Temporarily kept for backward compatibility */}
				<Route
					path="/dashboard"
					element={
						user ? (
							user.role === "admin" ? (
								<Navigate to="/admin" replace />
							) : user.role === "student" ? (
								<Navigate to="/student" replace />
							) : user.role === "technician" ? (
								<Navigate to="/technician" replace />
							) : (
								<Navigate to="/login" replace />
							)
						) : (
							<Navigate to="/login" replace />
						)
					}
				/>

				{/* Default Route */}
				<Route
					path="/"
					element={
						user ? (
							user.role === "admin" ? (
								<Navigate to="/admin" replace />
							) : user.role === "student" ? (
								<Navigate to="/student" replace />
							) : user.role === "technician" ? (
								<Navigate to="/technician" replace />
							) : (
								<Navigate to="/login" replace />
							)
						) : (
							<Navigate to="/login" replace />
						)
					}
				/>

				{/* Fallback */}
				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>
		</Router>
	);
}

/* ----------------------------- Protected Route ----------------------------- */
function AuthInitializer({ children }) {
	const [authReady, setAuthReady] = useState(false);
	const [authError, setAuthError] = useState(false);

	useEffect(() => {
		const initializeAuth = async () => {
			try {
				// Wait for persistence to be set
				const authPersistence = localStorage.getItem('authPersistence');
				const persistence = authPersistence === 'local' ? browserLocalPersistence : browserSessionPersistence;

				await setPersistence(auth, persistence);
				console.log(`🔐 Auth persistence set to: ${authPersistence === 'local' ? 'LOCAL' : 'SESSION'} (App level)`);

				setAuthReady(true);
			} catch (error) {
				console.error("❌ Failed to initialize auth:", error);
				setAuthError(true);
			}
		};

		initializeAuth();
	}, []);

	if (authError) {
		return (
			<div className="flex flex-col items-center justify-center h-screen bg-red-50">
				<div className="text-center p-6">
					<div className="text-red-600 text-6xl mb-4">❌</div>
					<h2 className="text-xl font-bold text-red-800 mb-2">Authentication Error</h2>
					<p className="text-red-600 mb-4">Failed to initialize authentication.</p>
					<button
						onClick={() => window.location.reload()}
						className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (!authReady) {
		return (
			<div className="flex items-center justify-center h-screen text-gray-600">
				Initializing authentication...
			</div>
		);
	}

	return children;
}

/* ----------------------------- Protected Route ----------------------------- */
function ProtectedRoute({ user, children }) {
	if (!user) return <Navigate to="/login" replace />;
	return children;
}

/* ----------------------------- Main App ----------------------------- */
export default function App() {
	return (
		<AuthInitializer>
			<AppContent />
		</AuthInitializer>
	);
}
